import { can, emp, me, used } from "../../services/access.js";
import { readDep, syncDraft } from "../../features/deployments/page.js";
import { ui, S, save } from "../store.js";
import { proj, siteById } from "../../features/sites/model.js";
import { minWage } from "../../domain/payroll.js";
import { today, fmtD, addDays } from "../../domain/dates.js";
import { P, peso } from "../../domain/format.js";
import { render } from "../shell.js";
import { log } from "../../services/audit.js";
import { toast } from "../../components/ui.js";
import { EMAIL_RE } from "../../features/admin/page.js";
import { client, issueInvoice } from "../../features/invoices/model.js";
import { readInv } from "../../features/invoices/components.js";
import { TIN_RE, LEAVE_CAP } from "../../domain/reference.js";
import { readJV } from "../../features/accounting/voucher-form.js";
import { finishCreation } from "../../components/creation-flow.js";
export function installSubmitEvents() {
  document.addEventListener("submit", (ev) => {
    ev.preventDefault();
    const g = (x) => document.getElementById(x)?.value?.trim() ?? "";
    if (ev.target.id === "depform") {
      if (!can("dep.manage")) return;
      readDep();
      const d = ui.depDraft,
        p = proj(d.project),
        mw = minWage(p.region, d.from || today()),
        num = (x) => +String(x ?? "").replace(/,/g, ""),
        rate = P(num(d.rate)),
        bill = P(num(d.bill)),
        pd = P(num(d.pd)),
        std = num(d.std);
      ui.depErr =
        d.emp === "NEW" && (!(d.name || "").trim() || !(d.trade || "").trim())
          ? "Enter the new hire's full name and trade."
          : !d.emp
            ? "Choose who to deploy."
            : rate <= 0
              ? "Enter the pay rate."
              : bill <= 0
                ? "Enter the bill rate to the client."
                : !d.from || !d.to || d.to <= d.from
                  ? "Enter the start and an expected end after it. A project contract needs both."
                  : d.type === "DAILY" && rate < mw.daily
                    ? `${peso(rate)} a day is below the ${peso(mw.daily)} minimum at ${p.site} (${mw.src}).`
                    : d.type === "PIECE" && (!(d.unit || "").trim() || std <= 0)
                      ? "Enter the unit and the standard output a day from the time-and-motion study."
                      : d.type === "PIECE" && Math.round(rate * std) < mw.daily
                        ? `At ${std} ${d.unit} a day this pays ${peso(Math.round(rate * std))}, below the ${peso(mw.daily)} minimum (Labor Code Art. 101). Raise the rate.`
                        : "";
      if (ui.depErr) return render();
      let e = emp(d.emp);
      if (d.emp === "NEW") {
        e = {
          id: "E" + String(S.employees.length + 1).padStart(3, "0"),
          name: d.name.trim(),
          position: d.trade.trim(),
          dept: "Site Operations",
          basic: 0,
          pay: "FIELD",
          hired: d.from,
          status: "PROJECT",
          lic: null,
          tin: "",
          sss: "",
          osh: false,
        };
        S.employees.push(e);
      }
      const dep = {
        id: "DP-" + String(++S.seq.dep).padStart(4, "0"),
        emp: e.id,
        project: p.code,
        type: d.type,
        rate,
        unit: d.type === "PIECE" ? d.unit.trim() : "",
        std: d.type === "PIECE" ? std : 0,
        bill,
        perDiem: pd,
        from: d.from,
        to: d.to,
        contract: !!d.contract,
        rest: 0,
        status: "ACTIVE",
      };
      S.deployments.push(dep);
      syncDraft();
      log(
        "Deployed personnel",
        `${e.name} to ${p.code} ${p.site} at ${peso(rate)}/${dep.unit || "day"}`,
      );
      ui.depDraft = null;
      ui.depErr = "";
      ui.dtab = "personnel";
      save();
      render();
      toast(`${e.name} deployed to ${p.site}`);
      return;
    }
    if (ev.target.id === "regform") {
      if (!can("dep.manage") && !can("sys.admin")) return;
      const kind = g("rg-kind"),
        no = g("rg-no"),
        until = g("rg-until");
      ui.rgErr = !no
        ? "Enter the registration or license number."
        : !until || until <= today()
          ? "Enter a validity date after today."
          : "";
      if (ui.rgErr) return render();
      S.company[kind] = {
        status: kind === "dole" ? "REGISTERED" : "LICENSED",
        no,
        until,
      };
      log(
        kind === "dole"
          ? "Recorded DOLE registration"
          : "Recorded PCAB license",
        `No. ${no}, valid to ${fmtD(until)}`,
      );
      ui.rgErr = "";
      save();
      render();
      toast("Saved");
      return;
    }
    if (ev.target.id === "userform") {
      if (!can("admin.users")) return;
      const name = g("nu-name"),
        email = g("nu-email").toLowerCase(),
        role = g("nu-role"),
        em = g("nu-emp"),
        exp = g("nu-exp");
      ui.invUser = { name, email, role, expires: exp };
      ui.uErr = !name
        ? "Enter the person's full name."
        : !EMAIL_RE.test(email)
          ? "Enter a valid work email."
          : S.users.some((u) => u.email === email)
            ? "That email already has an account."
            : exp && exp <= today()
              ? "Access must end after today."
              : "";
      if (ui.uErr) return render();
      S.users.push({
        id: "u" + (Date.now() % 1e6),
        name,
        email,
        role,
        emp: em || null,
        status: "INVITED",
        mfa: false,
        lastSeen: null,
        expires: exp || undefined,
        title: em ? undefined : "Created user profile",
      });
      log(
        "Created user profile",
        `${name} (${email}) as ${S.roles[role].name}`,
      );
      ui.invUser = null;
      save();
      render();
      toast(
        `Profile created for ${email}. Send their invitation below the user list.`,
      );
      return;
    }
    if (ev.target.id === "project-form") {
      if (!can("gl.create")) return;
      const data = new FormData(ev.target),
        code = String(data.get("code") || "")
          .trim()
          .toUpperCase(),
        name = String(data.get("name") || "").trim(),
        site = siteById(data.get("site")),
        cid = data.get("client"),
        lead = data.get("lead"),
        contract = Number(data.get("contract"));
      ui.projectError = !/^[A-Z]{3}$/.test(code)
        ? "Use exactly three letters for the project code."
        : S.projects.some((p) => p.code === code)
          ? "That project code already exists."
          : !name ||
              !site ||
              !client(cid) ||
              (lead && !emp(lead)) ||
              !Number.isFinite(contract) ||
              contract < 0
            ? "Complete the project details."
            : "";
      if (ui.projectError) return render();
      S.projects.push({
        code,
        name,
        siteId: site.id,
        site: site.name,
        region: site.region,
        cid,
        client: client(cid).name,
        lead,
        contract: P(contract),
        pct: 0,
        recog: 0,
        billed: 0,
        hb: 0,
        ha: 0,
      });
      log("Created project", `${code} at ${site.code}`);
      ui.projectError = "";
      save();
      finishCreation();
      render();
      toast(`${code} added to ${site.code}`);
      return;
    }
    if (ev.target.id === "roleform") {
      if (!can("admin.users")) return;
      const name = g("nr-name"),
        from = g("nr-from");
      ui.rErr = !name
        ? "Name the role."
        : Object.values(S.roles).some(
              (r) => r.name.toLowerCase() === name.toLowerCase(),
            )
          ? "A role with that name exists."
          : "";
      if (ui.rErr) return render();
      const key = "R" + (Date.now() % 1e6);
      S.roles[key] = {
        name,
        perms: S.roles[from].perms.filter((p) => p !== "sys.admin"),
      };
      log("Created role", `${name}, copied from ${S.roles[from].name}`);
      ui.rErr = "";
      save();
      finishCreation();
      render();
      toast(`${name} created. Adjust its rights in the matrix.`);
      return;
    }
    if (ev.target.id === "reopenform") {
      if (!can("sys.admin")) return;
      const why = g("reopen-reason"),
        qs = ["2026-03-31", "2026-06-30", "2026-09-30"],
        i = qs.indexOf(S.lockedThrough);
      ui.loErr =
        why.length < 10
          ? "Give a reason of at least a few words; it goes in the audit log."
          : i <= 0
            ? "There is no earlier quarter to reopen to."
            : "";
      if (ui.loErr) return render();
      const prev = S.lockedThrough;
      S.lockedThrough = qs[i - 1];
      log(
        "Reopened period",
        `${fmtD(prev)} → ${fmtD(S.lockedThrough)}: ${why}`,
      );
      ui.loErr = "";
      save();
      render();
      toast(`Books reopened after ${fmtD(S.lockedThrough)}`);
      return;
    }
    if (ev.target.id === "limitform") {
      if (!can("sys.admin")) return;
      const v = P(+g("lim-jv").replace(/,/g, "") || 0);
      if (v <= 0) {
        toast("Enter a limit above zero");
        return;
      }
      S.settings.jvLimit = v;
      log("Changed approval limit", `Vouchers above ${peso(v)} need the Owner`);
      save();
      render();
      toast("Approval limit saved");
      return;
    }
    if (ev.target.id === "secform") {
      if (!can("sys.admin")) return;
      const st = S.settings;
      st.mfaRequired = document.getElementById("sec-mfa").checked;
      st.sessionMin = +g("sec-sess");
      st.pwMin = +g("sec-pw");
      st.ipAllow = g("sec-ip");
      log(
        "Changed security policy",
        `Two-factor ${st.mfaRequired ? "required" : "optional"}, ${st.sessionMin}-minute timeout, ${st.pwMin}-character passwords`,
      );
      save();
      render();
      toast("Security policy saved");
      return;
    }
    if (ev.target.id === "invform") {
      readInv();
      const d = ui.invDraft;
      if (d.client === "NEW") {
        const n = (d.ncName || "").trim(),
          tin = (d.ncTin || "").trim(),
          ad = (d.ncAddr || "").trim();
        ui.invErr = !n
          ? "Enter the client's registered name."
          : !TIN_RE.test(tin)
            ? "Enter the client's TIN as 000-000-000-00000 (the last block is the branch code)."
            : !ad
              ? "Enter the client's business address."
              : "";
        if (ui.invErr) return render();
        const c = {
          id: "C-" + crypto.randomUUID(),
          name: n,
          tin,
          address: ad,
          vat: true,
        };
        S.clients.push(c);
        d.client = c.id;
        log("Added client", `${n} TIN ${tin}`);
      }
      const r = issueInvoice(d);
      if (typeof r === "string") {
        ui.invErr = r;
        return render();
      }
      if (d.depKey) S.depBilled[d.depKey] = r.no;
      ui.invDraft = null;
      ui.invErr = "";
      ui.invOpen = r.no;
      save();
      render();
      toast(`${r.no} issued and posted`);
      return;
    }
    if (ev.target.id === "coform") {
      const g = (x) => document.getElementById(x).value.trim(),
        tin = g("co-tin"),
        ad = g("co-addr"),
        nm = g("co-name");
      ui.coErr = !nm
        ? "Enter the registered name."
        : !TIN_RE.test(tin)
          ? "Enter the TIN as 000-000-000-00000 (the last block is the branch code)."
          : !ad
            ? "Enter the registered business address."
            : "";
      if (ui.coErr) return render();
      Object.assign(S.company, {
        name: nm,
        style: g("co-style"),
        tin,
        address: ad,
        rdo: g("co-rdo"),
      });
      ui.coEdit = false;
      log("Updated registration", `TIN ${tin}`);
      save();
      render();
      toast("Registration saved. New invoices print these details.");
      return;
    }
    if (ev.target.id === "jvform") {
      readJV();
      const d = ui.jvDraft,
        lines = d.lines
          .map((l) => ({ a: l.a, d: P(+l.d || 0), c: P(+l.c || 0) }))
          .filter((l) => l.d || l.c);
      const td = lines.reduce((a, l) => a + l.d, 0),
        tc = lines.reduce((a, l) => a + l.c, 0);
      ui.err = !d.memo.trim()
        ? "Add the particulars so the voucher explains itself."
        : lines.length < 2
          ? "A voucher needs at least two lines."
          : lines.some((l) => l.d && l.c)
            ? "Put each amount in either debit or credit, not both."
            : lines.some((l) => l.d < 0 || l.c < 0)
              ? "Amounts must be positive."
              : td !== tc
                ? `Debits ${peso(td)} and credits ${peso(tc)} must be equal.`
                : d.date <= S.lockedThrough
                  ? `Books are locked through ${fmtD(S.lockedThrough)}. Date the voucher after that.`
                  : "";
      if (ui.err) return render();
      const jv = {
        id: "JV-" + String(++S.seq.je).padStart(4, "0"),
        date: d.date,
        project: d.project || "",
        memo: d.memo.trim(),
        lines,
        status: "DRAFT",
        by: ui.user,
        src: "manual",
      };
      S.je.push(jv);
      ui.jvDraft = null;
      log("Prepared voucher", `${jv.id} ${jv.memo}`);
      save();
      render();
      toast(`${jv.id} saved for approval`);
    }
    if (ev.target.id === "lvform") {
      const e = me().emp,
        type = document.getElementById("lv-type").value,
        from = document.getElementById("lv-from").value,
        to = document.getElementById("lv-to").value,
        reason = document.getElementById("lv-reason").value.trim();
      let days = 0;
      for (let d = from; d && d <= to; d = addDays(d, 1))
        if (
          type === "MAT" ||
          ![0].includes(new Date(d + "T00:00:00Z").getUTCDay())
        )
          days++;
      const left = LEAVE_CAP[type] - used(e, type);
      ui.lvErr =
        !from || !to || to < from
          ? "Choose a start date and an end date on or after it."
          : days > left
            ? `That is ${days} day(s); you have ${left} left for this leave type.`
            : "";
      if (ui.lvErr) return render();
      S.leaves.push({
        id: "LV-" + ++S.seq.lv,
        emp: e,
        type,
        from,
        to,
        days,
        status: "PENDING",
        reason,
      });
      log("Filed leave", `${type} ${from} to ${to}`);
      save();
      finishCreation();
      render();
      toast("Leave request sent for approval");
    }
  });
}
